import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2, Plus, Loader2, Monitor, Users, Zap, Gift } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSlaveToolsPage = () => {
  const { token } = useAuth();
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [newSN, setNewSN] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [togglingFree, setTogglingFree] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [toolsRes, usersRes] = await Promise.all([
        axios.get(`${API}/mms/user-tools`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
      ]);
      setTools(toolsRes.data.tools || []);
      setUsers(usersRes.data.users || usersRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAssign = async () => {
    if (!selectedUser) return toast.error('Selecciona un usuario');
    if (!newSN.trim()) return toast.error('Introduce el Serial Number');
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append('user_id', selectedUser);
      fd.append('machine_sn', newSN.trim().toUpperCase());
      fd.append('label', newLabel.trim() || newSN.trim().toUpperCase());
      await axios.post(`${API}/mms/user-tools`, fd, { headers });
      toast.success('Herramienta asignada');
      setNewSN('');
      setNewLabel('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al asignar'); }
    finally { setAdding(false); }
  };

  const handleDelete = async (toolId) => {
    try {
      await axios.delete(`${API}/mms/user-tools/${toolId}`, { headers });
      setTools(prev => prev.filter(t => t.id !== toolId));
      toast.success('Herramienta eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleToggleFree = async (userId, currentFree) => {
    setTogglingFree(userId);
    try {
      const fd = new FormData();
      fd.append('free', !currentFree);
      await axios.put(`${API}/mms/user-free/${userId}`, fd, { headers });
      // Update local state
      setTools(prev => prev.map(t => t.user_id === userId ? { ...t, mms_free: !currentFree } : t));
      toast.success(!currentFree ? 'Servicio MMS gratuito activado' : 'Servicio MMS de pago activado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cambiar'); }
    finally { setTogglingFree(null); }
  };

  // Group tools by user
  const grouped = tools.reduce((acc, t) => {
    const key = t.user_id;
    if (!acc[key]) acc[key] = { email: t.user_email || t.user_id, tools: [], mms_free: t.mms_free || false };
    acc[key].tools.push(t);
    acc[key].mms_free = t.mms_free || false;
    return acc;
  }, {});

  if (loading) return <Layout><div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-white">GESTIÓN SLAVE TOOLS</h1>
        </div>

        {/* Assign new tool */}
        <div className="bg-surface border border-white/10 rounded-md p-5 mb-6">
          <p className="text-white font-heading text-sm font-bold mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" /> ASIGNAR HERRAMIENTA
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Usuario</label>
              <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
                className="w-full h-10 px-3 rounded-md bg-surface-highlight border border-white/10 text-white text-sm">
                <option value="">Seleccionar usuario...</option>
                {users.filter(u => !u.is_admin).map(u => (
                  <option key={u.id} value={u.id}>{u.company_name} ({u.email})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-zinc-500 text-xs mb-1 block">Serial Number (SN)</label>
              <Input value={newSN} onChange={e => setNewSN(e.target.value)} placeholder="Ej: 655F1CF1C3D4A902"
                className="bg-surface-highlight border-white/10 text-white font-mono" />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-zinc-500 text-xs mb-1 block">Etiqueta (opcional)</label>
            <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Ej: FLEX del taller principal"
              className="bg-surface-highlight border-white/10 text-white" />
          </div>
          <Button onClick={handleAssign} disabled={adding} className="bg-primary hover:bg-primary/90">
            {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Asignar
          </Button>
        </div>

        {/* Existing assignments grouped by user */}
        <div className="bg-surface border border-white/10 rounded-md p-5">
          <p className="text-white font-heading text-sm font-bold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> ASIGNACIONES ({tools.length})
          </p>
          {tools.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">No hay herramientas asignadas aún.</p>
          ) : (
            <div className="space-y-5">
              {Object.entries(grouped).map(([userId, data]) => (
                <div key={userId} className="border border-white/5 rounded-md p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-zinc-300 text-sm font-medium">{data.email}</p>
                    <button
                      onClick={() => handleToggleFree(userId, data.mms_free)}
                      disabled={togglingFree === userId}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                        data.mms_free
                          ? 'bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20'
                          : 'bg-surface-highlight border border-white/10 text-zinc-500 hover:border-white/20 hover:text-zinc-300'
                      }`}
                    >
                      {togglingFree === userId ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Gift className="w-3 h-3" />
                      )}
                      {data.mms_free ? 'GRATUITO' : 'DE PAGO'}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {data.tools.map(t => (
                      <div key={t.id} className="flex items-center justify-between p-3 bg-surface-highlight rounded-md">
                        <div className="flex items-center gap-3">
                          <Monitor className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">{t.label}</p>
                            <p className="text-zinc-600 text-xs font-mono">{t.machine_sn}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminSlaveToolsPage;
